import { VoidCallback } from '@deqo/foundation';
import { differenceInMinutes } from 'date-fns';
import React, { useMemo } from 'react';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { useLoader } from '../../../platform/Hook';
import { Button } from '../../../ui';
import { useResolve } from '../../Container';
import { Copy, Flexer } from '../../dashboard/elements/LayoutElements';
import { useEditorSessionObserver } from '../EditorSession';
import { MyProfileInfo } from '../MyProfileInfo';
import { MyRoleInfo } from '../MyRoleInfo';
import { ParticipantManager } from '../participants/ParticipantManager';
import { meetingSetHosts } from './MeetingCommands';
import { useActiveMeeting } from './MeetingHooks';

const HostName = ({ userId }: { userId: string }) => {
    const api = useResolve(ApiClient);
    const ptpts = useResolve(ParticipantManager);
    const [_, user] = useLoader(async () => (ptpts.get(userId) || (await api.exec(v2.users.list({ in: [userId] })))[0]), [userId]);
    return <span>{user?.name}</span>;
};

const HostMessage = () => {
    const { facilitators: hosts } = useActiveMeeting();

    if (hosts.length === 0) {
        return <Copy flush>No meeting host is set, all workspace members will act as host.</Copy>;
    }
    else if (hosts.length === 1) {
        return <Copy flush>Your meeting host is <HostName userId={hosts[0]} />.</Copy>;
    }
    else {
        return <Copy flush>
            <span>Your meeting hosts are:</span>
            <ol>
                {hosts.map((x, i) => <li key={i}><HostName userId={x} /></li>)}
            </ol>
        </Copy>;
    }
};

const useCanTakeOverMeeting = () => {
    const ptpts = useResolve(ParticipantManager);
    const meeting = useActiveMeeting();
    const hosts = useMemo(() => meeting.facilitators.map(x => ptpts.get(x)), [meeting.facilitators]);
    const { canManage } = useEditorSessionObserver(MyRoleInfo);

    // Can't manage? Can't take over
    if (!canManage) return false;
    // If no hosts, then can't take over
    if (hosts.length === 0) return false;
    // If any host is active, then can't take over
    if (hosts.some(x => !!x && x.presence !== 'offline')) return false;
    // If any host has been seen in the last 10 minutes, then can't take over
    if (hosts.some(x => !!x && differenceInMinutes(new Date(), x.lastSeenAt) < 10)) return false;
    // Otherwise, can take over
    return true;
};

const HostTakeOver = () => {
    const me = useEditorSessionObserver(MyProfileInfo);
    const dispatch = useResolve(CommandDispatcher);
    const { facilitators: hosts } = useActiveMeeting();
    const onTakeOver = () => {
        dispatch(meetingSetHosts, [...hosts, me.id]);
    };
    return <Flexer flex="column" gap="1" center>
        <Copy flush align="center">Looks like the meeting host has left.  Do you want to take over as host?</Copy>
        <Button variant="primary" onAction={onTakeOver}>Take Over Meeting</Button>
    </Flexer>;
};

export const MeetingParticipantView = ({ onClose }: { onClose: VoidCallback }) => {
    const cto = useCanTakeOverMeeting();
    return <Flexer padding="1 2" width="24" flex="column" center>
        {cto ? <HostTakeOver /> : <HostMessage />}
    </Flexer>;
};
